<script setup>

import {onMounted, ref, watch} from "vue";

const props = defineProps({
    inputs: {
        type: Object,
        required: true,
    },
    formula: {
        type: String,
        required: false,
    },
    options: {
        type: Object,
        required: true,
    },
    version: {
        type: Number,
        default: 1,
    },
})

const resultValidates = ref(true)
const showResult = ref(false)
const result = ref(null)
const results = ref({})
const displayText = ref('')
const _inputs = ref({})

const compileInputs = () => {
    _inputs.value = {}
    Object.values(props.inputs).forEach((value, key) => {
        _inputs.value[value.name] = {
            name: value.name,
            label: value.label,
            inputValue: value.defaultValue
        }
    })
}

const calculateResult = () => {
    if (props.version === 1) {
        calculateV1()
    } else {
        calculateV2()
    }
}

const calculateV1 = () => {
    result.value = 0
    const variables = {};
    try {
        for (const key in props.inputs) {
            variables[key] = _inputs.value[key].inputValue;
        }

        const formulaFunction = new Function(...Object.keys(variables), `return ${props.formula};`);

        showResult.value = true
        resultValidates.value = true
        result.value = formulaFunction(...Object.values(variables)).toFixed(props.options.decimals);
    } catch (e) {
        console.error('Calculation failed')
        showResult.value = true
        resultValidates.value = false
        return null
    }
}

const calculateV2 = () => {
    results.value = {}
    const calculations = props.options.calculations || {}
    const inputValues = {}

    try {
        // Extract input values
        for (const key in props.inputs) {
            inputValues[key] = _inputs.value[key].inputValue;
        }

        // Calculate each named calculation in order
        for (const [name, calc] of Object.entries(calculations)) {
            let formula = calc.formula || ''

            // Substitute input values
            for (const [fieldname, value] of Object.entries(inputValues)) {
                formula = formula.replace(new RegExp(`\\{${fieldname}\\}`, 'g'), value)
            }

            // Substitute previously calculated values
            for (const [prevName, prevValue] of Object.entries(results.value)) {
                formula = formula.replace(new RegExp(`\\{${prevName}\\}`, 'g'), prevValue)
            }

            if (formula.trim()) {
                const formulaFunction = new Function(`return ${formula};`)
                results.value[name] = formulaFunction()
            }
        }

        // Apply template
        displayText.value = applyTemplate(props.options.template || '', results.value, calculations, inputValues)

        showResult.value = true
        resultValidates.value = true
    } catch (e) {
        console.error('Calculation failed:', e)
        showResult.value = true
        resultValidates.value = false
        displayText.value = 'Calculation error, check your values and formulas.'
    }
}

const applyTemplate = (template, calculationResults, calculations, inputValues) => {
    let result = template

    // Replace calculation results with formatting
    for (const [name, value] of Object.entries(calculationResults)) {
        const decimals = calculations[name]?.decimals || 0
        const formatted = Number(value).toFixed(decimals)

        // Replace ${name|decimals} or ${name}
        result = result.replace(new RegExp(`\\$\\{${name}\\|\\d+\\}`, 'g'), '$' + formatted)
        result = result.replace(new RegExp(`\\$\\{${name}\\}`, 'g'), '$' + formatted)
        result = result.replace(new RegExp(`\\{${name}\\}`, 'g'), value)
    }

    // Replace input values
    for (const [name, value] of Object.entries(inputValues)) {
        result = result.replace(new RegExp(`\\{${name}\\}`, 'g'), value)
    }

    return result
}

watch(() => props.inputs, () => {
    compileInputs()
}, {deep: true})

onMounted(() => {
    compileInputs()
})

</script>

<template>
    <div class="bg-white rounded-wl p-8">
        <div v-if="! showResult">
            <div>
                <div
                    v-for="(input, i) in _inputs"
                    :key="i"
                >
                    <input-number
                        :label="input.label"
                        v-model:content="input.inputValue"
                        step="0.1"
                    ></input-number>
                </div>
            </div>
            <div class="flex justify-center">
                <button-primary
                    :disabled="!Object.keys(_inputs).length"
                    @click="calculateResult"
                >
                    {{ props.options.buttonLabelCalculate }}
                </button-primary>
            </div>
        </div>
        <div v-if="showResult">
            <!-- Version 1 Result Display -->
            <div v-if="props.version === 1">
                <div
                    v-if="! isNaN(result) && resultValidates"
                    class="text-center"
                >
                    <p
                        v-if="props.options.title"
                        class="text-lg"
                    >
                        {{ props.options.title }}
                    </p>
                    <div class="text-xl font-bold mb-4">
                        {{ props.options.prefix}}
                        {{ result }}
                        {{ props.options.suffix}}
                    </div>
                    <p
                        v-if="props.options.byline"
                    >
                        {{ props.options.byline }}
                    </p>
                </div>
                <div
                    v-else
                    class="mb-4 text-center"
                >
                    Calculation error, check your values and formula.
                </div>
            </div>

            <!-- Version 2 Result Display -->
            <div v-else>
                <div
                    v-if="resultValidates"
                    class="text-center"
                >
                    <p
                        v-if="props.options.title"
                        class="text-lg mb-2"
                    >
                        {{ props.options.title }}
                    </p>
                    <div class="text-lg mb-4 whitespace-pre-line">
                        {{ displayText }}
                    </div>
                    <p
                        v-if="props.options.byline"
                        class="text-sm text-gray-600"
                    >
                        {{ props.options.byline }}
                    </p>
                </div>
                <div
                    v-else
                    class="mb-4 text-center"
                >
                    {{ displayText }}
                </div>
            </div>

            <div class="flex justify-center mt-4">
                <button-primary
                    @click="showResult = false"
                >
                    {{ props.options.buttonLabelBack }}
                </button-primary>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
